import {Action, Card, closeCurrentDialog, DataStatus, openDialog} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  OfferPriceResponseBody,
  OfferResponseBody,
  OfferSaleVehicleResponseBody,
} from '@dms/api/finance';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {EditVehiclePriceForm, usePricePermissions, useSaleVehicleActions} from '@dms/shared';

import {buildArray, TestIdProps} from 'shared/web';

import {BusinessCaseSaleVehicleShortPrices} from './components/BusinessCaseSaleVehicleShortPrices';
import {BusinessCaseSaleVehicleSummaryPrices} from './components/BusinessCaseSaleVehicleSummaryPrices';
import {useBusinessCasePriceLogic} from './hooks/useBusinessCasePriceLogic';
import {useBusinessCaseSaleVehicleData} from './hooks/useBusinessCaseSaleVehicleData';

export type BusinessCaseSaleVehiclePriceSummaryType = {
  saleVehicle: OfferSaleVehicleResponseBody;
  businessCaseId: string;
  isBrokeredSale: string | null;
  offer?: OfferResponseBody;
  offerTotalPrice?: OfferPriceResponseBody | null;
} & TestIdProps;

export const BusinessCaseSaleVehiclePriceSummary = ({
  isBrokeredSale,
  ...props
}: BusinessCaseSaleVehiclePriceSummaryType) => {
  const {saleVehicleData, saleVehiclePrices, businessCaseData, isLoading, isError} =
    useBusinessCaseSaleVehicleData({
      businessCaseId: props.businessCaseId,
      saleVehicleId: props.saleVehicle?.vehicleId ?? '',
    });

  const permissions = usePricePermissions({
    businessCaseRecordId: props.businessCaseId,
    vehicleRecordId: props.saleVehicle?.vehicleId,
  });

  const businessCaseLogic = useBusinessCasePriceLogic({
    businessCaseData,
    saleVehicleData,
    saleVehicle: props.saleVehicle,
    isBrokeredSale,
  });

  const {isSaleActionEnabled} = useSaleVehicleActions(props.saleVehicle?.vehicleId ?? '');

  const handleEditPriceClick = () => {
    openDialog(
      <EditVehiclePriceForm
        saleVehicle={saleVehicleData}
        onClose={closeCurrentDialog}
        isPurchasePriceHidden
        data-testid={props['data-testid']}
        businessCaseId={props.businessCaseId}
      />,
      {
        title: i18n.t('entity.vehicle.actions.editPrice'),
        'data-testid': testIds.businessCase.selling('editPriceDialog'),
      }
    );
  };

  const actions = buildArray<Action>().when(
    isSaleActionEnabled('SALE_VEHICLE_UPDATE') &&
      permissions.canEditVehicleSalePrice &&
      businessCaseLogic.isOpenBusinessCase,
    {
      title: i18n.t('entity.vehicle.actions.editPrice'),
      type: 'button',
      leftIcon: 'image/edit',
      variant: 'link',
      onClick: handleEditPriceClick,
      'data-testid': testIds.businessCase.selling('openEditPriceDialog-button'),
    }
  );

  const commonProps = {
    saleVehicleData,
    saleVehiclePrices,
    businessCaseData,
    saleVehicle: props.saleVehicle,
    offerTotalPrice: props.offerTotalPrice,
    permissions,
    businessCaseLogic,
    isBrokeredSale,
    'data-testid': props['data-testid'],
  };

  return (
    <Card
      variant="inlineGrey"
      actions={actions}
      title={i18n.t`page.businessCase.labels.salVehiclePriceSummary`}
      isExpandable
    >
      <DataStatus
        isEmpty={isNilOrEmpty(saleVehiclePrices)}
        isLoading={isLoading}
        isError={isError}
        minHeight={10}
      >
        <Show whenFeatureDisabled={featureFlags.SALES_VEHICLE_PRICE_SUMMARY}>
          <BusinessCaseSaleVehicleShortPrices {...commonProps} />
        </Show>
        <Show whenFeatureEnabled={featureFlags.SALES_VEHICLE_PRICE_SUMMARY}>
          <BusinessCaseSaleVehicleSummaryPrices {...commonProps} />
        </Show>
      </DataStatus>
    </Card>
  );
};
